import { useState } from 'react';
import { createTransactie, type CreateTransactieBody } from '../../lib/invoices';

const METHODS: Array<CreateTransactieBody['methode']> = [
  'Bankrekening',
  'Pin',
  'Contant',
  'iDEAL',
  'Mollie',
];

interface TransactieCreateFormProps {
  parentType: 'Werk_Order__c' | 'Invoice__c';
  parentId: string;
  defaultAmount: number;
  onSuccess: () => void;
  onCancel: () => void;
}

function toLocalDateTimeInputValue(d: Date): string {
  // Produces yyyy-MM-ddTHH:mm for <input type="datetime-local" />.
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function TransactieCreateForm({
  parentType,
  parentId,
  defaultAmount,
  onSuccess,
  onCancel,
}: TransactieCreateFormProps) {
  const [bedrag, setBedrag] = useState<string>(
    defaultAmount > 0 ? defaultAmount.toFixed(2) : ''
  );
  const [methode, setMethode] = useState<CreateTransactieBody['methode']>('Bankrekening');
  const [datum, setDatum] = useState<string>(toLocalDateTimeInputValue(new Date()));
  const [opmerking, setOpmerking] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const bedragNum = Number(bedrag.replace(',', '.'));
    if (!Number.isFinite(bedragNum) || bedragNum <= 0) {
      setError('Bedrag moet groter dan 0 zijn');
      return;
    }
    const datumDate = new Date(datum);
    if (Number.isNaN(datumDate.getTime())) {
      setError('Ongeldige datum');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createTransactie({
        parentType,
        parentId,
        bedrag: bedragNum,
        methode,
        datum: datumDate.toISOString(),
        opmerking: opmerking.trim() || undefined,
      });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="px-4 py-4 bg-slate-50 border-t border-slate-200 space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">Bedrag</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={bedrag}
            onChange={(e) => setBedrag(e.target.value)}
            disabled={submitting}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">Methode</span>
          <select
            value={methode}
            onChange={(e) => setMethode(e.target.value as CreateTransactieBody['methode'])}
            disabled={submitting}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-slate-600 block mb-1">Datum & tijd</span>
        <input
          type="datetime-local"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          disabled={submitting}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium text-slate-600 block mb-1">Opmerking (optioneel)</span>
        <input
          type="text"
          value={opmerking}
          onChange={(e) => setOpmerking(e.target.value)}
          disabled={submitting}
          placeholder="bijv. bankreferentie of reden"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </label>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting ? 'Bezig…' : 'Registreer transactie'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
        >
          Annuleren
        </button>
      </div>
    </form>
  );
}
